import React from 'react';

export type LibraryViewOption<T extends string> = {
  value: T;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
};

type LibraryViewToggleProps<T extends string> = {
  value: T;
  options: readonly LibraryViewOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
};

/**
 * Shared labelled view selector for library and calendar toolbars.
 *
 * Labels remain visible at phone widths so the control does not rely on icon
 * interpretation. The shared CSS owns touch sizing and selected/focus states.
 */
export function LibraryViewToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel = 'View mode',
  className,
}: LibraryViewToggleProps<T>) {
  const classes = ['library-toolbar__view', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="group" aria-label={ariaLabel}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            className={`btn xs ${selected ? 'primary' : 'btn-outline'}`}
            aria-label={option.label}
            aria-pressed={selected}
            data-active={selected ? 'true' : 'false'}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            title={`${option.label} view`}
          >
            {option.icon ? (
              <span className="library-toolbar__view-icon" aria-hidden="true">
                {option.icon}
              </span>
            ) : null}
            <span className="library-toolbar__view-label">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export default LibraryViewToggle;
